import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Save, ArrowLeft, X, Plus } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { HtmlContentUploader } from '../components/HtmlContentUploader';
import { Button } from '../../components';
import { articlesService } from '../../services';

const FormContainer = styled.form`
  max-width: 800px;
  margin: 0 auto;
`;

const FormHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1.5rem;

  label {
    font-weight: 600;
    color: ${props => props.theme.colors.text.primary};
    margin-bottom: 0.5rem;
    font-size: ${props => props.theme.typography.small};
  }

  input, textarea, select {
    padding: 0.75rem 1rem;
    border: 1px solid ${props => props.theme.colors.border.medium};
    border-radius: ${props => props.theme.borderRadius.md};
    font-size: ${props => props.theme.typography.body};
    font-family: inherit;
    background-color: ${props => props.theme.colors.background.primary};
    color: ${props => props.theme.colors.text.primary};
    transition: border-color ${props => props.theme.transitions.fast};

    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.primary};
    }

    &::placeholder {
      color: ${props => props.theme.colors.text.tertiary};
    }
  }

  textarea {
    resize: vertical;
    min-height: 100px;
  }

  &.full-width {
    grid-column: 1 / -1;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;

  input[type="checkbox"] {
    width: auto;
    margin: 0;
  }

  label {
    margin: 0;
    font-weight: 500;
  }
`;


const ImagePreview = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  border-radius: ${props => props.theme.borderRadius.md};
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  margin-bottom: 1rem;

  .remove-btn {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background-color: rgba(239, 68, 68, 0.9);
    color: white;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Tag = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: ${props => props.theme.colors.primaryLight};
  color: ${props => props.theme.colors.primary};
  padding: 0.5rem 0.75rem;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.small};

  button {
    background: none;
    border: none;
    color: inherit;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
  }
`;

const TagInput = styled.div`
  display: flex;
  gap: 0.5rem;

  input {
    flex: 1;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding-top: 2rem;
  border-top: 1px solid ${props => props.theme.colors.border.light};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: ${props => props.theme.colors.text.secondary};
`;

export const ArticleForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    tags: [],
    featuredImage: '',
    publishDate: '',
    published: false
  });

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    if (isEditing) {
      fetchArticle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditing]);

  const fetchArticle = async () => {
    try {
      const article = await articlesService.getArticleById(id);
      setFormData({
        ...article,
        tags: article.tags || []
      });
    } catch (error) {
      console.error('Error fetching article:', error);
      alert('Failed to load article');
      navigate('/admin/articles');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const removeImage = () => {
    setFormData(prev => ({
      ...prev,
      featuredImage: ''
    }));
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      alert('Please enter an article title');
      return;
    }

    if (!formData.content.trim()) {
      alert('Please add article content');
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await articlesService.updateArticle(id, formData);
        alert('Article updated successfully!');
      } else {
        const newId = await articlesService.createArticle(formData);
        alert('Article created successfully!');
        navigate(`/admin/articles/${newId}/edit`);
      }
    } catch (error) {
      console.error('Error saving article:', error);
      alert('Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title={isEditing ? 'Edit Article' : 'New Article'}>
        <LoadingContainer>
          <p>Loading article...</p>
        </LoadingContainer>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isEditing ? 'Edit Article' : 'New Article'}>
      <FormContainer onSubmit={handleSubmit}>
        <FormHeader>
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate('/admin/articles')}
          >
            <ArrowLeft size={20} />
            Back to Articles
          </Button>
        </FormHeader>

        <FormGrid>
          <FormGroup>
            <label htmlFor="title">Article Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter article title"
              required
            />
          </FormGroup>

          <FormGroup>
            <label htmlFor="publishDate">Publish Date</label>
            <input
              type="date"
              id="publishDate"
              name="publishDate"
              value={formData.publishDate}
              onChange={handleChange}
            />
          </FormGroup>

          <FormGroup className="full-width">
            <label htmlFor="excerpt">Article Excerpt *</label>
            <textarea
              id="excerpt"
              name="excerpt"
              value={formData.excerpt}
              onChange={handleChange}
              placeholder="Brief description for article cards and previews"
              required
            />
          </FormGroup>
        </FormGrid>

        <FormGroup>
          <label>Featured Image URL</label>
          <input
            type="url"
            name="featuredImage"
            value={formData.featuredImage}
            onChange={handleChange}
            placeholder="Paste image URL (e.g. https://example.com/image.jpg)"
          />
          {formData.featuredImage && (
            <ImagePreview image={formData.featuredImage}>
              <button
                type="button"
                className="remove-btn"
                onClick={removeImage}
              >
                <X size={16} />
              </button>
            </ImagePreview>
          )}
        </FormGroup>

        <FormGroup>
          <label>Tags</label>
          <TagsContainer>
            {formData.tags.map((tag, index) => (
              <Tag key={index}>
                {tag}
                <button type="button" onClick={() => removeTag(tag)}>
                  <X size={14} />
                </button>
              </Tag>
            ))}
          </TagsContainer>
          <TagInput>
            <input
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              placeholder="Add a tag"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
            />
            <Button type="button" onClick={addTag} variant="outline">
              <Plus size={16} />
              Add
            </Button>
          </TagInput>
        </FormGroup>

        <FormGroup>
          <label>Article Content *</label>
          <HtmlContentUploader
            value={formData.content}
            onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
            placeholder="Upload your Google Doc exported as HTML..."
          />
        </FormGroup>

        <FormGroup>
          <CheckboxGroup>
            <input
              type="checkbox"
              id="published"
              name="published"
              checked={formData.published}
              onChange={handleChange}
            />
            <label htmlFor="published">
              Publish article (make it visible on the public site)
            </label>
          </CheckboxGroup>
        </FormGroup>

        <FormActions>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/articles')}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            <Save size={20} />
            {saving ? 'Saving...' : (isEditing ? 'Update Article' : 'Create Article')}
          </Button>
        </FormActions>
      </FormContainer>
    </AdminLayout>
  );
};